import React from "react";
import Card from "components/card";
import Widget from "components/widget/Widget";
import { useNavigate } from "react-router-dom";
import {
  MdLocalShipping,
  MdAssignment,
  MdScale,
  MdInventory,
  MdWaterDrop,
  MdBarChart,
  MdTrendingUp,
} from "react-icons/md";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import Loading from "components/loading";
import { useRcnDeliveries } from "hooks/useRcnDeliveries";

const RCN_TYPE_COLORS = {
  Organic: "#10b981",
  "Fair Trade": "#3b82f6",
  Conventional: "#f59e0b",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { dispatches, loading, error } = useRcnDeliveries();

  // Calculate summary metrics from dispatch data
  const summaryMetrics = {
    totalDispatches: dispatches.length,
    totalWeight: dispatches.reduce((acc, d) => acc + (Number(d.weight) || 0), 0),
    totalQuantity: dispatches.reduce((acc, d) => acc + (Number(d.quantity) || 0), 0),
    avgMoisture:
      dispatches.length > 0
        ? (
            dispatches.reduce((acc, d) => acc + (parseFloat(d.moisture) || 0), 0) /
            dispatches.length
          ).toFixed(1)
        : "0",
  };

  // RCN Type distribution for pie chart
  const rcnDistribution = Object.entries(
    dispatches.reduce((acc, d) => {
      const t = d.rcn_type || "Other";
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  // Dispatches by month for bar chart
  const dispatchesByDate = Object.entries(
    dispatches.reduce((acc, d) => {
      const date = d.dispatch_date ? d.dispatch_date.slice(0, 7) : null; // YYYY-MM
      if (date) acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([date, count]) => ({ date, dispatches: count }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // Recent dispatches (latest 5)
  const recentDispatches = [...dispatches]
    .sort((a, b) => new Date(b.dispatch_date) - new Date(a.dispatch_date))
    .slice(0, 5);

  return (
    <div className="mt-3">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
          RCN Dispatch Summary
        </h2>
        <button
          onClick={() => navigate("/admin/rcn-delivery/form")}
          className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          <MdAssignment className="h-5 w-5" />
          New Dispatch
        </button>
      </div>

      {loading && (
        <div className="mb-6">
          <Loading text="Loading dispatch data…" />
        </div>
      )}
      {error && !loading && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Widget
          icon={<MdLocalShipping className="h-7 w-7" />}
          title="Total Dispatches"
          subtitle={summaryMetrics.totalDispatches.toLocaleString()}
        />
        <Widget
          icon={<MdScale className="h-7 w-7" />}
          title="Total Weight"
          subtitle={summaryMetrics.totalWeight.toLocaleString()}
        />
        <Widget
          icon={<MdInventory className="h-7 w-7" />}
          title="Total Quantity"
          subtitle={summaryMetrics.totalQuantity.toLocaleString()}
        />
        <Widget
          icon={<MdWaterDrop className="h-7 w-7" />}
          title="Avg Moisture"
          subtitle={`${summaryMetrics.avgMoisture}%`}
        />
      </div>

      {/* Charts Row */}
      <div className="mb-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* RCN Type Distribution */}
        <Card extra="p-6">
          <div className="mb-4 flex items-center gap-2">
            <MdBarChart className="h-6 w-6 text-brand-500" />
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Dispatches by RCN Type
            </h3>
          </div>
          {rcnDistribution.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rcnDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {rcnDistribution.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={
                          RCN_TYPE_COLORS[entry.name] ||
                          `hsl(${index * 120}, 70%, 50%)`
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} dispatch(es)`]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
              No dispatch data yet
            </div>
          )}
        </Card>

        {/* Dispatches Over Time */}
        <Card extra="p-6">
          <div className="mb-4 flex items-center gap-2">
            <MdTrendingUp className="h-6 w-6 text-brand-500" />
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Dispatches by Month
            </h3>
          </div>
          {dispatchesByDate.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dispatchesByDate}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(val) => {
                      const [y, m] = val.split("-");
                      return new Date(y, m - 1).toLocaleDateString("en-GB", {
                        month: "short",
                        year: "2-digit",
                      });
                    }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(val) => {
                      const [y, m] = val.split("-");
                      return new Date(y, m - 1).toLocaleDateString("en-GB", {
                        month: "long",
                        year: "numeric",
                      });
                    }}
                  />
                  <Bar
                    dataKey="dispatches"
                    name="Dispatches"
                    fill="#4318ff"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
              No dispatch data yet
            </div>
          )}
        </Card>
      </div>

      {/* Recent Dispatches Table */}
      <Card extra="overflow-hidden p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Recent Dispatches
          </h3>
          <button
            onClick={() => navigate("/admin/rcn-delivery/records")}
            className="text-sm font-medium text-brand-500 hover:text-brand-600"
          >
            View all →
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-navy-600">
                <th className="pb-3 pt-1 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Date
                </th>
                <th className="pb-3 pt-1 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  RCN Type
                </th>
                <th className="pb-3 pt-1 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Truck
                </th>
                <th className="pb-3 pt-1 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Driver
                </th>
                <th className="pb-3 pt-1 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Departure Weight
                </th>
                <th className="pb-3 pt-1 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Arrival Weight
                </th>
                <th className="pb-3 pt-1 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Qty
                </th>
                <th className="pb-3 pt-1 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Moisture %
                </th>
              </tr>
            </thead>
            <tbody>
              {recentDispatches.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    No dispatch data yet
                  </td>
                </tr>
              ) : (
                recentDispatches.map((d) => (
                  <tr
                    key={d.id}
                    className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-800/50"
                  >
                    <td className="whitespace-nowrap py-4 text-sm text-navy-700 dark:text-white">
                      {d.dispatch_date ? new Date(d.dispatch_date).toLocaleDateString("en-GB") : "—"}
                    </td>
                    <td className="whitespace-nowrap py-4">
                      <span
                        className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                        style={{
                          backgroundColor: `${RCN_TYPE_COLORS[d.rcn_type] || "#6b7280"}20`,
                          color: RCN_TYPE_COLORS[d.rcn_type] || "#6b7280",
                        }}
                      >
                        {d.rcn_type}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-4 text-sm text-navy-700 dark:text-white">
                      {d.truck_number}
                    </td>
                    <td className="whitespace-nowrap py-4 text-sm text-navy-700 dark:text-white">
                      {d.driver_name}
                    </td>
                    <td className="whitespace-nowrap py-4 text-right text-sm text-navy-700 dark:text-white">
                      {d.weight != null ? Number(d.weight).toLocaleString() : "—"}
                    </td>
                    <td className="whitespace-nowrap py-4 text-right text-sm text-navy-700 dark:text-white">
                      {d.weight_of_arrival != null ? Number(d.weight_of_arrival).toLocaleString() : "—"}
                    </td>
                    <td className="whitespace-nowrap py-4 text-right text-sm text-navy-700 dark:text-white">
                      {d.quantity != null ? Number(d.quantity).toLocaleString() : "—"}
                    </td>
                    <td className="whitespace-nowrap py-4 text-right text-sm text-navy-700 dark:text-white">
                      {d.moisture != null ? `${d.moisture}%` : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
